import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment-timezone';

import { PageHeader } from '../../../components/PageHeader';
import { TextInput } from '../../../components/PageHeader/TextInput';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../constants';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import { dateFilter, getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { DateFilters } from '../../../utils/DateFilters';
import actions from './redux/actions';
import {
  GET_SUPERUSER_HEALTH_ALERT_INCIDENTS,
} from './redux/constants';
import { DETECTOR_KEYS } from 'modals/EditRuleModal';
import { toolTipForTable } from 'utils';

function stringifyIncidentContext(value) {
  if (value == null) {
    return '-';
  }
  if (typeof value === 'string') {
    return value || '-';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch (error) {
    return String(value);
  }
}

function AlertIncidents(props) {
  const { getAlertIncidents, refreshTimestamp, filters } = props;
  
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  const globalPageSize = useGlobalPageSize();
  const tableRef = useRef(null);
  const latestRequestId = useRef(0);
  
  const dateFiltersForPage = { ...DateFilters };
  delete dateFiltersForPage.Custom;
  
  const pageRequest = useRef({
    offset: 0,
    limit: globalPageSize,
  });

  const fetchData = () => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    const request = { ...pageRequest.current };
    setIsLoading(true);
    
    getAlertIncidents(request).then(resp => {
      if (requestId !== latestRequestId.current) return;
      if (resp.type === `${GET_SUPERUSER_HEALTH_ALERT_INCIDENTS}/result`) {
        setData(resp.response?.data || []);
        setPagination({
          totalRecords: resp.response?.totalRecords ?? 0,
          limit: resp.response?.limit ?? request.limit,
          offset: resp.response?.offset ?? request.offset,
        });
      }
      setIsLoading(false);
    }).catch(() => {
      if (requestId === latestRequestId.current) {
        setIsLoading(false);
      }
    });
    
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    pageRequest.current.offset = 0;
    fetchData();
  }, [refreshTimestamp]);

  // Filter handling
  const onFiltersChange = React.useMemo(() => _.debounce(filterState => {
    // Handle incident status filter
    const statusFilters = getRelevantFilters(filterState.filter(f => f.group === 'incidentStatus'));
    if (statusFilters?.length > 0 && statusFilters[0].value !== 'all') {
      pageRequest.current.status = statusFilters[0].value;
    } else {
      delete pageRequest.current.status;
    }
    
    // Handle date filter
    const dateFilters = getRelevantFilters(filterState.filter(f => f.group === 'dateFilter'));
    if (dateFilters?.length > 0) {
      pageRequest.current.startDate = DateFilters[dateFilters[0].value]?.dates.startDate();
      pageRequest.current.endDate = DateFilters[dateFilters[0].value]?.dates.endDate();
    } else {
      delete pageRequest.current.startDate;
      delete pageRequest.current.endDate;
    }

    pageRequest.current.offset = 0;
    fetchData();
  }, 1000), []);

  React.useEffect(() => {
    const statusFilters = getRelevantFilters(filters.filter(f => f.group === 'incidentStatus'));
    const currentStatusValue = statusFilters?.length > 0 ? statusFilters[0].value : 'all';
    
    const dateFilters = getRelevantFilters(filters.filter(f => f.group === 'dateFilter'));
    let startDate;
    let endDate;
    if (dateFilters?.length > 0) {
      startDate = DateFilters[dateFilters[0].value]?.dates.startDate();
      endDate = DateFilters[dateFilters[0].value]?.dates.endDate();
    }

    if ((pageRequest.current.status !== currentStatusValue && currentStatusValue !== 'all') || 
        (pageRequest.current.startDate !== startDate) || 
        (pageRequest.current.endDate !== endDate)) {
      onFiltersChange(filters);
    }
  }, [filters]);

  // Search
  const searchQueryDebounced = _.debounce(query => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    fetchData();
  }, 1000);

  // Table handlers
  const onOffsetChange = value => {
    pageRequest.current.offset = value;
    fetchData();
  };

  const onPageSizeChange = value => {
    pageRequest.current.limit = value;
    pageRequest.current.offset = 0;
    fetchData();
  };

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) return;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    pageRequest.current.offset = 0;
    fetchData();
  };

  // Columns
  const columns = React.useMemo(() => [
    columnHelper.accessor('id', {
      header: 'Incident ID',
      cell: info => info.getValue() || '-',
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor('rule_id', {
      header: 'Rule ID',
      cell: info => info.getValue() || '-',
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor('detector_key', {
      header: 'Detector Key',
      cell: info => info.getValue() ? DETECTOR_KEYS.find(d => d.value === info.getValue())?.label : '',
      size: 200,
      enableSorting: false,
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: info => info.getValue() ? toolTipForTable(info.getValue()) : '-',
      size: 300,
      enableSorting: false,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => info.getValue() || '-',
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      cell: info => info.getValue() || '-',
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor('opened_at', {
      header: 'Opened At',
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('resolved_at', {
      header: 'Resolved At',
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('last_evaluated_at', {
      header: 'Last Evaluated At',
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('last_metric_value', {
      header: 'Last Metric Value',
      cell: info => info.getValue() || '-',
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor('last_notified_at', {
      header: 'Last Notified At',
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('last_attempted_at', {
      header: 'Last Attempted At',
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 180,
      enableSorting: false,
    }),
    columnHelper.accessor('context', {
      header: 'Context',
      cell: info => {
        const contextText = stringifyIncidentContext(info.getValue());
        return contextText !== '-' ? toolTipForTable(contextText, contextText) : '-';
      },
      size: 300,
      enableSorting: false,
    }),
    columnHelper.accessor('delivery_attempts', {
      header: 'Delivery Attempts',
      cell: info => info.getValue() || '-',
      size: 150,
      enableSorting: false,
    }),
  ], []);

  // Render
  const pageHeaderLeft = isLoading 
    ? 'Loading...'
    : `${pagination.totalRecords ?? data.length} Incidents`;

  const pageHeaderRight = (
    <>
      <TextInput 
        className="search" 
        placeholder="Search incidents..." 
        onChange={e => searchQueryDebounced(e.target.value)} 
      />
      <AdvancedFilters>
        <Filter definition={dateFilter(dateFiltersForPage).dateFilter} />
        <Filter definition={getFiltersDefinition().incidentStatus} />
      </AdvancedFilters>
    </>
  );

  return (
    <div className="alert-incidents-page">
      <PageHeader left={pageHeaderLeft} right={pageHeaderRight} />
      <AdvancedFiltersBar customFilters={dateFilter(dateFiltersForPage)} />
      <Table3
        isLoading={isLoading}
        name="alert-incidents"
        data={data}
        pagination={pagination}
        columns={columns}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
        enableColumnFiltering
        ref={tableRef}
        noScroll
        pinFirstColumn
      />
    </div>
  );
}

AlertIncidents.propTypes = {
  getAlertIncidents: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any.isRequired,
  filters: PropTypes.array.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getAlertIncidents: pageRequest => dispatch(actions.getAlertIncidents(pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AlertIncidents);
